import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI, categoriesAPI } from '../../utils/api';
import { localized } from '../../utils/localized';
import ProductCard from '../../components/shop/ProductCard';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isAr = lang === 'ar';

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'created_at';
  const page = parseInt(searchParams.get('page') || '1');
  const featured = searchParams.get('featured') || '';
  const bestseller = searchParams.get('bestseller') || '';

  useEffect(() => {
    categoriesAPI.getAll().then(setCategories);
  }, []);

  useEffect(() => {
    setLoading(true);
    productsAPI.getAll({ category, search, sort, page, limit: 12, featured, bestseller })
      .then(data => { setProducts(data.products); setTotal(data.total); setPages(data.pages); })
      .finally(() => setLoading(false));
  }, [category, search, sort, page, featured, bestseller]);

  const updateParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  // Localized name for the active category (used in the page header)
  const activeCategory = category ? categories.find(c => c.slug === category) : null;
  const pageTitle = activeCategory
    ? (localized(activeCategory, 'name', lang) || activeCategory.name || t('shop.title'))
    : featured ? t('shop.featuredProducts')
    : bestseller ? t('shop.bestsellers')
    : search ? t('shop.searchResultsFor', { query: search })
    : t('shop.allProducts');

  // "X products" — pick singular/plural manually (i18next pluralization config-free)
  const productsCount = total === 1
    ? t('shop.productsCount', { count: total })
    : t('shop.productsCountPlural', { count: total });

  return (
    <div className="shop-page" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Page Header */}
      <div className="shop-page__hero">
        <div className="container">
          <h1>{pageTitle}</h1>
          <p className="shop-page__count">{productsCount}</p>
        </div>
      </div>

      <div className="container">
        <div className="shop-layout">
          {/* Sidebar Filters */}
          <aside className="shop-sidebar">
            <div className="shop-sidebar__section">
              <h4>{t('shop.categories')}</h4>
              <ul>
                <li>
                  <button className={!category ? 'active' : ''} onClick={() => updateParam('category', '')}>
                    {t('shop.allProducts')} <span>{products.length && !category ? total : ''}</span>
                  </button>
                </li>
                {categories.map(cat => (
                  <li key={cat.id}>
                    <button className={category === cat.slug ? 'active' : ''} onClick={() => updateParam('category', cat.slug)}>
                      {localized(cat, 'name', lang) || cat.name} <span>{cat.product_count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="shop-sidebar__section">
              <h4>{t('shop.sortBy')}</h4>
              <select value={sort} onChange={e => updateParam('sort', e.target.value)} className="form-control">
                <option value="created_at">{t('shop.sort.newest')}</option>
                <option value="base_price">{t('shop.sort.priceLowToHigh')}</option>
                <option value="name">{t('shop.sort.nameAZ')}</option>
              </select>
            </div>
          </aside>

          {/* Products */}
          <div className="shop-main">
            {/* Search bar */}
            <div className="shop-search">
              <input
                type="search" placeholder={t('shop.searchPlaceholder')} className="form-control"
                defaultValue={search}
                onKeyDown={e => e.key === 'Enter' && updateParam('search', e.target.value)}
              />
            </div>

            {loading ? (
              <div className="shop-loading">
                {[...Array(8)].map((_, i) => <div key={i} className="product-skeleton" />)}
              </div>
            ) : products.length === 0 ? (
              <div className="shop-empty">
                <span>🍯</span>
                <h3>{t('shop.noProducts')}</h3>
                <button className="btn btn-outline" onClick={() => setSearchParams({})}>{t('shop.clearFilters')}</button>
              </div>
            ) : (
              <div className="shop-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="shop-pagination">
                {[...Array(pages)].map((_, i) => (
                  <button
                    key={i} className={`shop-pagination__btn ${page === i + 1 ? 'active' : ''}`}
                    onClick={() => updateParam('page', i + 1)}
                  >{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .shop-page__hero {
          background: var(--olive-dark); color: white; padding: 60px 0 30px;
          margin-bottom: 0;
        }
        .shop-page__hero h1 { font-family: var(--font-serif); color: var(--gold-light); }
        .shop-page__count { color: rgba(255,255,255,0.6); margin-top: 4px; }
        .shop-layout { display: grid; grid-template-columns: 240px 1fr; gap: 40px; padding: 40px 0 60px; }
        .shop-sidebar__section { margin-bottom: 28px; }
        .shop-sidebar__section h4 { font-family: var(--font-serif); font-size: 1rem; margin-bottom: 14px; color: var(--olive); }
        .shop-sidebar__section ul { list-style: none; display: flex; flex-direction: column; gap: 4px; }
        .shop-sidebar__section ul li button {
          width: 100%; text-align: left; padding: 8px 12px; border-radius: 8px; font-size: 0.88rem;
          display: flex; justify-content: space-between; transition: background 0.2s;
        }
        [dir="rtl"] .shop-sidebar__section ul li button { text-align: right; }
        .shop-sidebar__section ul li button:hover, .shop-sidebar__section ul li button.active {
          background: var(--cream-dark); color: var(--gold); font-weight: 600;
        }
        .shop-sidebar__section ul li button span { color: var(--text-muted); font-size: 0.78rem; }
        .shop-search { margin-bottom: 24px; }
        .shop-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .shop-loading { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .product-skeleton {
          aspect-ratio: 0.85; background: linear-gradient(90deg, #f0e6d0 25%, #e8d9bc 50%, #f0e6d0 75%);
          background-size: 200% 100%; animation: shimmer 1.5s infinite;
          border-radius: var(--radius-lg);
        }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shop-empty { text-align: center; padding: 80px 20px; }
        .shop-empty span { font-size: 3rem; display: block; margin-bottom: 16px; }
        .shop-pagination { display: flex; gap: 8px; margin-top: 40px; justify-content: center; }
        .shop-pagination__btn {
          width: 40px; height: 40px; border-radius: 50%; border: 2px solid var(--border);
          font-weight: 600; transition: all 0.2s;
        }
        .shop-pagination__btn:hover, .shop-pagination__btn.active {
          background: var(--gold); border-color: var(--gold); color: white;
        }
        @media (max-width: 900px) { .shop-layout { grid-template-columns: 1fr; } .shop-sidebar { display: flex; gap: 20px; flex-wrap: wrap; } }
        @media (max-width: 600px) { .shop-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; } }
      `}</style>
    </div>
  );
};

export default ShopPage;
